import React, { useState } from "react";
import { I18n } from "react-redux-i18n";
import { Dropdown, Form, Button } from "semantic-ui-react";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";

import Modal from "../../../components/common/Modal";

import styles from "./TimePeriodSelector.module.css";

// Configuration constants
const MAX_DATE_RANGE_DAYS = 366; // Maximum allowed days in custom date range - adjust as needed

interface TimePeriodSelectorProps {
  selectedPeriod: "last7Days" | "last30Days" | "last90Days" | "ytd" | "customRange";
  customDateFrom?: string | null;
  customDateTo?: string | null;
  onPeriodChange: (period: "last7Days" | "last30Days" | "last90Days" | "ytd" | "customRange", dateFrom?: string, dateTo?: string) => void;
}

export const TimePeriodSelector: React.FC<TimePeriodSelectorProps> = ({
  selectedPeriod,
  customDateFrom,
  customDateTo,
  onPeriodChange
}) => {
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [tempDateFrom, setTempDateFrom] = useState<Date | null>(
    customDateFrom ? moment(customDateFrom).toDate() : null
  );
  const [tempDateTo, setTempDateTo] = useState<Date | null>(
    customDateTo ? moment(customDateTo).toDate() : null
  );

  const dropdownOptions = [
    { key: "last7Days", value: "last7Days", text: I18n.t("home.timePeriod.last7Days") },
    { key: "last30Days", value: "last30Days", text: I18n.t("home.timePeriod.last30Days") },
    { key: "last90Days", value: "last90Days", text: I18n.t("home.timePeriod.last90Days") },
    { key: "ytd", value: "ytd", text: I18n.t("home.timePeriod.ytd") },
    { key: "customRange", value: "customRange", text: I18n.t("home.timePeriod.customRange") }
  ];

  const handleDropdownChange = (event: React.SyntheticEvent, data: any) => {
    const newPeriod = data.value;

    if (newPeriod === "customRange") {
      // Open the modal for custom range selection
      setIsCustomModalOpen(true);
    } else {
      onPeriodChange(newPeriod);
    }
  };

  const handleCustomRangeApply = () => {
    if (tempDateFrom && tempDateTo) {
      const fromStr = moment(tempDateFrom).format("YYYY-MM-DD");
      const toStr = moment(tempDateTo).format("YYYY-MM-DD");
      onPeriodChange("customRange", fromStr, toStr);
      setIsCustomModalOpen(false);
    }
  };

  const handleCustomRangeCancel = () => {
    setTempDateFrom(customDateFrom ? moment(customDateFrom).toDate() : null);
    setTempDateTo(customDateTo ? moment(customDateTo).toDate() : null);
    setIsCustomModalOpen(false);
  };

  const handleEditCustomRange = () => {
    setIsCustomModalOpen(true);
  };

  const getSelectedText = () => {
    if (selectedPeriod === "customRange" && customDateFrom && customDateTo) {
      return `${moment(customDateFrom).format("D MMM YYYY")} - ${moment(customDateTo).format("D MMM YYYY")}`;
    }
    return dropdownOptions.find(option => option.value === selectedPeriod)?.text || "";
  };

  // Validation helpers
  const getDateRangeDays = () => {
    if (!tempDateFrom || !tempDateTo) return 0;
    return moment(tempDateTo).diff(moment(tempDateFrom), "days") + 1; // +1 to include both start and end days
  };

  const getValidationMessage = () => {
    if (!tempDateFrom && !tempDateTo) {
      return "Both start and end dates are required";
    }
    if (!tempDateFrom) {
      return "Start date is required";
    }
    if (!tempDateTo) {
      return "End date is required";
    }
    if (moment(tempDateFrom).isAfter(moment(tempDateTo))) {
      return "Start date must be before end date";
    }
    if (moment(tempDateTo).isAfter(moment())) {
      return "End date cannot be in the future";
    }
    const rangeDays = getDateRangeDays();
    if (rangeDays > MAX_DATE_RANGE_DAYS) {
      return `Maximum date range is ${MAX_DATE_RANGE_DAYS} days. Current selection: ${rangeDays} days`;
    }
    return null;
  };

  const isApplyDisabled = !tempDateFrom || !tempDateTo ||
    moment(tempDateFrom).isAfter(moment(tempDateTo)) ||
    moment(tempDateTo).isAfter(moment()) ||
    getDateRangeDays() > MAX_DATE_RANGE_DAYS;

  return (
    <div className={styles["timePeriodSelector"]}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Dropdown
          className={styles["timePeriodDropdown"]}
          selection
          value={selectedPeriod}
          options={dropdownOptions}
          onChange={handleDropdownChange}
          text={getSelectedText()}
        />

        {/* Conditional edit button - only show when custom range is selected */}
        {selectedPeriod === "customRange" && customDateFrom && customDateTo && (
          <button
            className={styles["editButton"]}
            onClick={handleEditCustomRange}
            title="Edit custom date range"
          >
            <svg height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 10L14 6M2.49997 21.5L5.88434 21.124C6.29783 21.078 6.50457 21.055 6.69782 20.9925C6.86926 20.937 7.03242 20.8586 7.18286 20.7594C7.35242 20.6475 7.49951 20.5005 7.7937 20.2063L21 7C22.1046 5.89543 22.1046 4.10457 21 3C19.8954 1.89543 18.1046 1.89543 17 3L3.7937 16.2063C3.49952 16.5005 3.35242 16.6475 3.24061 16.8171C3.1414 16.9676 3.06298 17.1307 3.00748 17.3022C2.94493 17.4954 2.92195 17.7021 2.87601 18.1156L2.49997 21.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        )}
      </div>

      <Modal
        size="mini"
        open={isCustomModalOpen}
        onClose={handleCustomRangeCancel}
        header={I18n.t("home.timePeriod.customRangeModal.title")}
        actions={[
          <Button
            fluid
            onClick={handleCustomRangeCancel}
          >
            {I18n.t("general.cancel")}
          </Button>,
          <Button
            primary
            fluid
            onClick={handleCustomRangeApply}
            disabled={isApplyDisabled}
          >
            {I18n.t("general.apply")}
          </Button>
        ]}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DatePicker
            inline
            selectsRange
            dateFormat="yyyy-MM-dd"
            endDate={tempDateTo}
            maxDate={new Date()}
            selected={tempDateFrom}
            startDate={tempDateFrom}
            onChange={dates => {
              const [start, end] = dates;
              setTempDateFrom(start);
              setTempDateTo(end);
            }}
          />
        </div>
      </Modal>
    </div>
  );
};
